/**
 * Auto-group favorite paths by quadrant with path-depth sorting.
 * Used by Manifest Mini and SidebarManifest for glow-separator layout.
 */

import { ACTIVE_ROUTE_MAP, getRouteByPath, type RouteEntry, type RouteQuadrant } from "./routeMapData"

const QUADRANT_ORDER: RouteQuadrant[] = ["core", "admin", "public", "creator"]

const QUADRANT_LABEL: Record<RouteQuadrant, string> = {
  core: "core",
  admin: "admin",
  creator: "creator",
  public: "public",
  redirect: "redirect",
}

/** Full Tailwind via-* class for separator gradient glow. */
const QUADRANT_GLOW_CLASS: Record<RouteQuadrant, string> = {
  core: "via-luster-400/30",
  admin: "via-amber-400/30",
  creator: "via-rose-400/30",
  public: "via-emerald-400/30",
  redirect: "via-stone-500/20",
}

export type FavoriteGroup = {
  quadrant: RouteQuadrant
  label: string
  /** Tailwind class for gradient (e.g. via-luster-400/30). */
  glowClass: string
  routes: RouteEntry[]
}

const ROUTE_INDEX = new Map<string, number>(ACTIVE_ROUTE_MAP.map((route, index) => [route.path, index]))

/**
 * Resolve favorite paths to ROUTE_MAP entries, group by quadrant,
 * order groups core → admin → creator → public (redirects excluded),
 * sort within each group by canonical ROUTE_MAP order.
 */
export function groupFavorites(paths: string[]): FavoriteGroup[] {
  const entries = paths
    .map((p) => getRouteByPath(p))
    .filter((r): r is RouteEntry => r !== undefined && !r.isRedirect)

  const byQuadrant = new Map<RouteQuadrant, RouteEntry[]>()
  for (const e of entries) {
    const list = byQuadrant.get(e.quadrant) ?? []
    list.push(e)
    byQuadrant.set(e.quadrant, list)
  }

  const groups: FavoriteGroup[] = []
  for (const q of QUADRANT_ORDER) {
    const routes = byQuadrant.get(q)
    if (!routes?.length) continue
    routes.sort((a, b) => {
      const aIdx = ROUTE_INDEX.get(a.path) ?? Number.MAX_SAFE_INTEGER
      const bIdx = ROUTE_INDEX.get(b.path) ?? Number.MAX_SAFE_INTEGER
      if (aIdx !== bIdx) return aIdx - bIdx
      return a.path.localeCompare(b.path)
    })
    groups.push({
      quadrant: q,
      label: QUADRANT_LABEL[q],
      glowClass: QUADRANT_GLOW_CLASS[q],
      routes,
    })
  }
  return groups
}
